<template>
  <div id="sticky-show" :class="['buybox', { 'buybox--second-turn': isSecondTurn, 'buybox--gift-card': isGiftCard }]">
    <div v-if="isDesktopScreen" class="buybox__media">
      <!-- Product Images or Gallery will go here -->
      <ImageGallery
        :product="product"
        :isDesktopScreen="isDesktopScreen"
        :showSlidesCount="false"
        :isGiftCard="isGiftCard"
        :disableZoom="!isDesktopScreen"
      />
      <WishlistCTA
        :product="product"
        :selectedVariantDetails="productForm"
        :isGreenVariant="true"
        customClass="buybox__pdp-wishlist-cta--desktop"
      />
    </div>

    <div class="buybox__details">
      <!-- Title, Price, Description, Color options, CTA -->
      <div class="buybox__primary-info">
        <BuyboxHeader
          :product="product"
          :productForm="productForm"
          :showWishlistCTA="!isDesktopScreen"
          :isSecondTurn="isSecondTurn"
          @see-description-click="scrollToDescription"
          :isDesktopScreen="isDesktopScreen"
        />
        <ImageGallery
          v-if="!isDesktopScreen"
          :product="product"
          :isDesktopScreen="isDesktopScreen"
          :showSlidesCount="true"
          :isGiftCard="isGiftCard"
          :disableZoom="!isDesktopScreen"
        />
        <BuyboxPurchaseInfo
          :product="product"
          :isSecondTurn="isSecondTurn"
          @on-option-change="onOptionChange"
        />
      </div>

      <!-- About the product, Materials, Features, Complete the look -->
      <div class="buybox__secondary-info" ref="buyboxProductDetails">
        <!-- Notice label i.e Nikwax -->
        <div v-if="product.product_notice?.content" class="buybox__product-notice">
          <ImageField
            v-if="product.product_notice?.icon"
            :key="product.product_notice.icon"
            :obj="{ url: product.product_notice.icon, alt: '', width: 100, height: 95 }"
            class="buybox__product-notice-image"
          />

          <p v-if="product.product_notice.content" v-html="product.product_notice.content" class="buybox__product-notice-description"></p>
        </div>

        <!-- Product detail accordions -->
        <Accordion
          v-if="product.product_details.length"
          :items="product.product_details"
          :isAccordionOpenByDefault="true"
          :singleToggleMode="true"
          :isScrollToView="true"
        >
          <template v-slot="{ item }">
            <div class="buybox__product-details-wrapper">
              <ImageField
                v-if="item.image"
                :key="item.image"
                :obj="{ url: item.image, alt: '', width: 130, height: 48 }"
                class="buybox__product-details-image"
              />

              <!-- Render main description -->
              <div
                v-if="item.description"
                v-html="item.description"
                class="buybox__product-details-description"
              ></div>

              <!-- Render image_with_content -->
              <div
                v-if="item.image_with_content?.length"
                class="buybox__product-details-features"
              >
                <div
                  v-for="(entry, index) in item.image_with_content"
                  :key="index"
                  class="buybox__product-details-feature"
                >
                  <ImageField
                    v-if="entry.icon"
                    :key="entry.icon"
                    :obj="{ url: entry.icon, alt: '', width: 24, height: 24 }"
                    class="buybox__product-details-feature-icon"
                  />
                  <div
                    v-html="entry.content"
                    class="buybox__product-details-feature-content"
                  ></div>
                </div>
              </div>
            </div>
          </template>
        </Accordion>

        <!-- Curated reviews -->
        <CuratedReviews
          :product="product"
          :isDesktopScreen="isDesktopScreen"
        />

        <!-- Complete the look -->
        <CompleteTheLook :ctlProducts="ctlProducts" />
      </div>
    </div>
  </div>
</template>

<script setup>
  import { onMounted, reactive, watch, computed, ref } from 'vue';
  import { useStore } from 'vuex';
  import ImageGallery from './image-gallery.vue';
  import BuyboxHeader from './buybox-header.vue';
  import BuyboxPurchaseInfo from './buybox-purchase-info.vue';
  import WishlistCTA from '../../global/wishlist-cta.vue';
  import Accordion from '../../global/accordion.vue';
  import ImageField from '../../global/image.vue';
  import CuratedReviews from './curated-reviews.vue';
  import CompleteTheLook from './complete-the-look.vue';
  import { useMediaType } from '../../../utils/use-media-type';
  import { MEDIA_TYPES } from '../../../utils/constants';

  const props = defineProps({
    product: {
      type: Object,
      default: () => ({}),
    },
  });

  const store = useStore();
  const mediaType = useMediaType();

  const product = reactive(props.product);
  const firstOption = reactive(product.options?.[0]?.name);
  const productForm = reactive(store.state?.pdp?.productForm);
  const buyboxProductDetails = ref(null);

  const isSecondTurn = computed(() => product.tags.includes('Second Turn'));
  const isGiftCard = computed(() => product.title.includes('E-Gift Card'));
  const selectedFirstLevelOption = computed(() => store.state?.pdp?.firstLevelOptions?.[product.id]);
  const additionalPropertyOptions = computed(() => product?.additional_property_options?.map((adntlOpt) => adntlOpt.value) || []);
  const isDesktopScreen = computed(() => [MEDIA_TYPES.TABLET_EXTRA_LARGE, MEDIA_TYPES.DESKTOP, MEDIA_TYPES.DESKTOP_LARGE].includes(mediaType.value));
  const ctlProducts = computed(() => {
    const { variants } = product;
    const selectedFirstOption = productForm[firstOption];

    if (!selectedFirstOption) {
      return [];
    }

    const selectedColorVariants = variants.filter(
      (variant) => variant.ctlProducts?.length > 0 && variant.option1 === selectedFirstOption
    );

    if (selectedColorVariants.length) {
      const filteredProducts = selectedColorVariants[0].ctlProducts
        .map((ctlProduct) => {
          const firstLevelOptions = ctlProduct.product.variants.reduce((set, variant) => {
            const property = variant.option1;
            const setItem = (set[property] = set[property] || {});
            if (variant.available) setItem.available = true;
            return set;
          }, {});

          const isAvailable = firstLevelOptions[ctlProduct.selectedFirstOption]?.available;
          return isAvailable ? ctlProduct : null;
        })
        .filter(Boolean);

      return filteredProducts;
    }

    return [];
  });

  onMounted(async () => {
    initialSetup();
  });

  const initialSetup = () => {
    const { options, variants } = product;
    const params = new window.URLSearchParams(window.location.search);
    const variantParam = params.get('variant');
    const colorParam = params.get('color');

    // Delete unnecessary parameters from the URL
    updateParams('delete');

    if (isSecondTurn.value || variantParam || options.length === 1) {
      // Find the selectedVariant or use the first variant
      const selectedVariant = variantParam ? variants.find((variant) => variant.id === variantParam) : variants[0];

      // Set the selectedVariant
      updateProductForm('selectedVariant', selectedVariant);

      // Set options based on the selectedVariant
      options.forEach((option, index) => {
        updateProductForm(option.name, selectedVariant[`option${index + 1}`]);
      });

      updateParams('update', selectedVariant);
    } else {
      if (colorParam) {
        updateProductForm('Color', colorParam);
      } else {
        watchSelectedFirstLevelOptionOnceReady();
      }

      // For Inseam and Outseam
      if (additionalPropertyOptions.value.length) {
        const ioData = options.find((ele) => ele.name === 'Inseam' || ele.name === 'Outseam');
        ioData && updateProductForm(ioData['name'], ioData['values'][0]);
      }
      updateProductForm('selectedVariant', null);
    }
  };

  const updateProductForm = (key, value) => {
    store.dispatch('updatePDPProductForm', {
      key: key,
      value: value,
    });
  };

  const scrollToDescription = () => {
    const target = buyboxProductDetails.value;
    const header = document.getElementById('site-header');
    const gallery = document.querySelector('.image-gallery--grid');
    const stickyBox = document.querySelector('.buybox__details');

    const headerHeight = header?.offsetHeight || 0;
    const targetTop = target.getBoundingClientRect().top + window.scrollY;
    let scrollToY;

    if (!gallery || isGiftCard.value || isSecondTurn.value) {
      scrollToY = targetTop - headerHeight;
    } else {
      const galleryScrollHeight = gallery.scrollHeight;
      const stickyHeight = stickyBox.offsetHeight;

      // If gallery is shorter than or equal to sticky, scroll normally
      if (galleryScrollHeight <= stickyHeight) {
        scrollToY = targetTop - headerHeight;
      } else {
        // Get where the sticky box stops sticking
        const galleryTopY = gallery.getBoundingClientRect().top + window.scrollY;
        const stickyUnstickScrollY = galleryTopY + galleryScrollHeight - stickyHeight;

        // Distance from top of sticky box to the target (within sticky)
        const stickyBoxTop = stickyBox.getBoundingClientRect().top + window.scrollY;
        const offsetInsideSticky = target.getBoundingClientRect().top + window.scrollY - stickyBoxTop;

        // Final scroll: release sticky, then scroll to target inside it
        scrollToY = stickyUnstickScrollY + offsetInsideSticky - headerHeight;
      }
    }

    window.scrollTo({
      top: scrollToY,
      behavior: 'smooth',
    });
  };

  const updateParams = (type, variant) => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    if (type === 'update' && variant) {
      params.set('variant', variant.id);
    } else if (type === 'delete') {
      params.delete('color');
    }

    url.search = params.toString();
    window.history.replaceState({ url: url.toString() }, null, url.toString());
  };

  const onOptionChange = (key, value, index, length) => {
    updateProductForm(key, value);
    updateVariantValue();

    if (key === 'Color') {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });
    }
  };

  const updateVariantValue = () => {
    const { options } = product;
    const variantValue = options.map((option) => productForm[option.name]).join(' / ');
    updateSelectedVariant(variantValue);
  };

  const updateSelectedVariant = (value) => {
    const variant = product.variants.find((item) => item.value === value);
    const selectedVariant = variant || null;
    updateProductForm('selectedVariant', selectedVariant);
    updateParams('update', productForm.selectedVariant);
  };

  const watchSelectedFirstLevelOptionOnceReady = () => {
    const unwatch = watch(selectedFirstLevelOption, (newVal) => {
      if (newVal?.value) {
        updateProductForm(firstOption, newVal.value);
        unwatch(); // Stop watching after the first execution
      }
    });
  };
</script>
